import React from 'react';
import { Users, Phone, BookOpen, BarChart2 } from 'lucide-react';

export default function StudentPreview({ students }) {
  if (!students || students.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center">
        <Users size={40} className="mx-auto text-gray-300 mb-3" />
        <p className="text-gray-500 text-sm font-medium">No students loaded yet</p>
        <p className="text-gray-400 text-xs mt-1">Upload an Excel file to preview students</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users size={16} className="text-blue-600" />
          <span className="font-semibold text-gray-700 text-sm">Student Preview</span>
        </div>
        <span className="text-xs font-medium bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full">
          {students.length} student{students.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide w-10">#</th>
              <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <div className="flex items-center gap-1"><Users size={11} />Name</div>
              </th>
              <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <div className="flex items-center gap-1"><Phone size={11} />Phone</div>
              </th>
              <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <div className="flex items-center gap-1"><BookOpen size={11} />Subject</div>
              </th>
              <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Marks</th>
              <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <div className="flex items-center gap-1 justify-center"><BarChart2 size={11} />%</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {students.map((student, idx) => {
              const pct = student.totalMarks > 0
                ? ((student.obtainedMarks / student.totalMarks) * 100).toFixed(1)
                : 0;
              const pctNum = parseFloat(pct);
              const pctColor =
                pctNum >= 75 ? 'text-green-600 bg-green-50' :
                pctNum >= 45 ? 'text-yellow-600 bg-yellow-50' :
                'text-red-600 bg-red-50';

              return (
                <tr
                  key={idx}
                  className={`border-b border-gray-100 hover:bg-blue-50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                >
                  <td className="px-3 py-2.5 text-gray-400 text-xs font-medium">{idx + 1}</td>
                  <td className="px-3 py-2.5 font-medium text-gray-800">{student.name || '—'}</td>
                  <td className="px-3 py-2.5 text-gray-600 font-mono text-xs">{student.phone || '—'}</td>
                  <td className="px-3 py-2.5 text-gray-600">{student.subject || '—'}</td>
                  <td className="px-3 py-2.5 text-center text-gray-700 font-medium">
                    {student.obtainedMarks}/{student.totalMarks}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${pctColor}`}>
                      {pct}%
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Summary row */}
      <div className="px-4 py-2.5 bg-blue-50 border-t border-blue-100 flex items-center justify-between text-xs text-blue-700">
        <span className="font-medium">Total: {students.length} records</span>
        <span className="text-gray-500">
          {students.filter(s => s.phone).length} with phone numbers
        </span>
      </div>
    </div>
  );
}
